// Health KPI bar — dashboard parity v3.3.
//
// Sits below the dashboard header, above the Employee Analytics
// group. Mirrors v1's "KPI strip": broker-wide ratios that flag
// data hygiene issues (commitments missing complexity, deadlines,
// assignees, descriptions, reviewers). Each pill is colour-coded
// by threshold and clicks through to the Tracker pre-filtered to
// the unhealthy subset so the user can drill into the offending
// rows.

import { ChevronDown, ChevronUp } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  useCommonFilters,
  useHealthKpisQuery,
  type HealthKpiMetric,
} from "@/features/dashboard";


export function HealthKpiBar() {
  const filters = useCommonFilters();
  const query = useHealthKpisQuery(filters);
  const [expanded, setExpanded] = useState(false);

  const metrics = query.data?.metrics ?? [];
  const unhealthy = useMemo(
    () => metrics.filter((m) => m.tier !== "healthy"),
    [metrics],
  );
  const healthyCount = metrics.length - unhealthy.length;

  if (query.isLoading || metrics.length === 0) {
    // Don't show a loading skeleton — the bar is decorative; render
    // nothing until data lands so it doesn't bump the layout.
    return null;
  }

  return (
    <section
      data-testid="dashboard-health-kpi-bar"
      data-expanded={expanded ? "true" : "false"}
      className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-raised p-3"
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {unhealthy.length === 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sage-tint px-2.5 py-0.5 text-[11px] font-semibold text-teal">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-teal" />
              All healthy ({metrics.length})
            </span>
          ) : (
            <>
              {unhealthy.map((m) => <KpiPill key={m.key} metric={m} />)}
              {healthyCount > 0 && (
                <span
                  data-testid="dashboard-health-kpi-healthy-count"
                  className="inline-flex items-center gap-1.5 rounded-full bg-sage-tint/60 px-2.5 py-0.5 text-[11px] font-medium text-teal"
                >
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-teal" />
                  {healthyCount} healthy
                </span>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          data-testid="dashboard-health-kpi-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-text-secondary hover:text-text-primary"
        >
          {expanded ? "Collapse" : "Show all"}
          {expanded ? (
            <ChevronUp size={12} aria-hidden="true" />
          ) : (
            <ChevronDown size={12} aria-hidden="true" />
          )}
        </button>
      </header>

      {expanded && (
        <ul
          role="list"
          data-testid="dashboard-health-kpi-grid"
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6"
        >
          {metrics.map((m) => <KpiCard key={m.key} metric={m} />)}
        </ul>
      )}
    </section>
  );
}


function KpiPill({ metric }: { metric: HealthKpiMetric }) {
  const palette = tierPalette(metric.tier);
  const value = ratioOrCount(metric);
  const inner = (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
        palette.pill,
      ].join(" ")}
    >
      <span aria-hidden="true" className={["h-1.5 w-1.5 rounded-full", palette.dot].join(" ")} />
      <span>{metric.label}</span>
      <span className="text-text-tertiary">·</span>
      <span>{value}</span>
    </span>
  );
  if (metric.drill_through_url) {
    return (
      <Link
        to={metric.drill_through_url}
        data-testid={`dashboard-health-kpi-pill-${metric.key}`}
        className="hover:opacity-90"
      >
        {inner}
      </Link>
    );
  }
  return (
    <span data-testid={`dashboard-health-kpi-pill-${metric.key}`}>{inner}</span>
  );
}


function KpiCard({ metric }: { metric: HealthKpiMetric }) {
  const palette = tierPalette(metric.tier);
  const value = ratioOrCount(metric);
  const ratioCopy =
    metric.denominator != null
      ? `${metric.numerator} of ${metric.denominator}`
      : `${metric.numerator}`;
  return (
    <li
      data-testid={`dashboard-health-kpi-card-${metric.key}`}
      className={[
        "flex flex-col gap-0.5 rounded-md border-l-2 bg-canvas/40 px-3 py-2",
        palette.cardStripe,
      ].join(" ")}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {metric.label}
      </span>
      <span className={["text-base font-semibold tabular-nums", palette.cardValue].join(" ")}>
        {value}
      </span>
      <span className="text-[10px] text-text-tertiary">
        {ratioCopy}
      </span>
    </li>
  );
}


function ratioOrCount(metric: HealthKpiMetric): string {
  if (metric.ratio != null) return `${Math.round(metric.ratio * 100)}%`;
  return `${metric.numerator}`;
}


function tierPalette(tier: HealthKpiMetric["tier"]) {
  if (tier === "alert") {
    return {
      pill: "bg-ember-tint text-ember-ink",
      dot: "bg-ember",
      cardStripe: "border-ember",
      cardValue: "text-ember-ink",
    };
  }
  if (tier === "warn") {
    return {
      pill: "bg-gold-tint text-gold-ink",
      dot: "bg-gold",
      cardStripe: "border-gold",
      cardValue: "text-gold-ink",
    };
  }
  return {
    pill: "bg-sage-tint text-teal",
    dot: "bg-teal",
    cardStripe: "border-teal",
    cardValue: "text-teal",
  };
}
